import React, { createContext, useContext, useState, useCallback } from 'react';
import { storage } from "./firebaseConfig";
import { ref, getDownloadURL } from "firebase/storage";
import { getApiUrl, whenAppConfigReady } from "./appConfig";
import { lerItemCardapio } from "../utils/cardapioPadrao";

const RestauranteContext = createContext();

// Sem acento: é a chave que os itens gravam em diasSelecionados.
const diasDaSemana = ["segunda", "terca", "quarta", "quinta", "sexta", "sabado", "domingo"];

export const RestauranteProvider = ({ children }) => {
    const [cache, setCache] = useState({});

    const checkIfOpen = useCallback((openingHours) => {
        try {
            if (!openingHours ||
                typeof openingHours !== 'object' ||
                !openingHours.days ||
                !openingHours.openHour ||
                !openingHours.openMinute ||
                !openingHours.closeHour ||
                !openingHours.closeMinute) {
                return false;
            }

            const now = new Date();
            const currentDay = now.getDay();
            const currentTime = now.getHours() * 60 + now.getMinutes();

            const daysArray = openingHours.days.split(',').map(Number);
            const openHour = parseInt(openingHours.openHour, 10);
            const openMinute = parseInt(openingHours.openMinute, 10);
            const closeHour = parseInt(openingHours.closeHour, 10);
            const closeMinute = parseInt(openingHours.closeMinute, 10);

            const openTime = openHour * 60 + openMinute;
            const closeTime = closeHour * 60 + closeMinute;

            const isOvernight = openTime > closeTime;
            
            if (isOvernight) {
                if (currentTime >= openTime) {
                    return daysArray.includes(currentDay);
                } else if (currentTime < closeTime) {
                    const previousDay = currentDay === 0 ? 6 : currentDay - 1;
                    return daysArray.includes(previousDay);
                }
                return false;
            } else {
                return daysArray.includes(currentDay) && currentTime >= openTime && currentTime < closeTime;
            }
        } catch (e) {
            console.error("Erro ao verificar horário:", e);
            return false;
        }
    }, []);

    const processItems = useCallback(async (rawItems) => {
        const items = [];
        const cats = new Set(['todos']);

        await Promise.all(
            (rawItems || []).map(async (rawItem) => {
                // Qualquer formato (Auuii antigo, espelho iFood, padrão novo) vira o mesmo
                // item — sem isto, item do iFood ficava sem `tipo` e fora das categorias.
                const itemData = lerItemCardapio(rawItem, rawItem?.id);
                try {
                    // Só resolve no Storage se ainda não tiver imageUrl (a maioria já tem).
                    if (itemData.imagePath && !itemData.imageUrl) {
                        const imageRef = ref(storage, itemData.imagePath);
                        itemData.imageUrl = await getDownloadURL(imageRef);
                    }

                    if (itemData.tipo === 'marmitex' && itemData.diasSelecionados) {
                        diasDaSemana.forEach(dia => {
                            if (itemData.diasSelecionados[dia]?.selecionado) {
                                cats.add(`marmitex-${dia}`);
                            }
                        });
                    }

                    const item = { ...itemData };
                    items.push(item);
                    if (item.tipo) cats.add(item.tipo.toLowerCase());

                } catch (e) {
                    console.error("Erro ao processar item:", e);
                    items.push({ ...itemData });
                }
            })
        );

        return {
            items,
            categories: Array.from(cats).sort((a, b) => {
                if (a === 'todos') return -1;
                if (b === 'todos') return 1;
                if (a.startsWith('marmitex') && b.startsWith('marmitex')) {
                    return diasDaSemana.indexOf(a.split('-')[1]) - diasDaSemana.indexOf(b.split('-')[1]);
                }
                if (a.startsWith('marmitex')) return -1;
                if (b.startsWith('marmitex')) return 1;
                return a.localeCompare(b);
            })
        };
    }, []);

    const getRestauranteData = useCallback(async (restauranteId) => {
        if (cache[restauranteId]) {
            return cache[restauranteId];
        }

        try {
            // Tela pública (sem login): espera a URL da API vir do settings/publicConfig
            // antes de montar a requisição — ver Confg/appConfig.js.
            await whenAppConfigReady();
            // Vitrine pública via backend (Admin SDK) — o Firestore mantém
            // `merchants`/`cardapio` fechados para o client SDK. O backend já
            // remove campos sensíveis e aplica o fallback de rating.
            const res = await fetch(`${getApiUrl()}/api/public/stores/${restauranteId}/menu`);
            if (!res.ok) {
                throw new Error("Restaurante não encontrado");
            }
            const data = await res.json();
            const restData = data.restaurante || {};

            const isOpen = checkIfOpen(restData.openingHours || {});
            const { items, categories } = await processItems(data.cardapio || []);

            const newData = {
                restaurante: restData,
                isOpen,
                cardapio: items,
                categories
            };

            setCache(prev => ({ ...prev, [restauranteId]: newData }));
            return newData;
        } catch (error) {
            console.error("Erro no fetch contextual: ", error);
            throw error;
        }
    }, [cache, checkIfOpen, processItems]);

    const invalidateCache = useCallback((restauranteId) => {
        setCache(prev => {
            const next = { ...prev };
            delete next[restauranteId];
            return next;
        });
    }, []);

    return (
        <RestauranteContext.Provider value={{ getRestauranteData, invalidateCache, cache }}>
            {children}
        </RestauranteContext.Provider>
    );
};

export const useRestauranteContext = () => useContext(RestauranteContext);
